import { useCallback, useEffect } from 'react';

import {
	hudStore,
	modeStore,
	mapStore,
	locationStore,
	displayStore,
	goalStore,
	systemStore,
	soundStore,
	loggerStore,
	welcomeStore,
	remoteStore,
	versionStore,
	frameStore,
	missionStore,
	autonomousKartStore,
} from '../../store/sceneStore';

import _ from 'lodash';
import { useSendUserInput, useSocket } from '../../provider/socketContext';

import { useCheckVersion } from './updateInfo/useCheckVersion';
import { useUpdateHudInfo } from './updateInfo/useUpdateHudInfo';
import { useUpdateModeInfo } from './updateInfo/useUpdateModeInfo';
import { useUpdateRemoteMode } from './updateInfo/useUpdateRemoteMode';
import { useUpdateLocationInfo } from './updateInfo/useUpdateLocationInfo';
import { useUpdateGoalInfo } from './updateInfo/useUpdateGoalInfo';
import { useUpdateSystemInfo } from './updateInfo/useUpdateSystemInfo';
import { useUpdateLoggerInfo } from './updateInfo/useUpdateLoggerInfo';
import { useUpdateMapInfo } from './updateInfo/useUpdateMapInfo';
import { useUpdateDisplayInfo } from './updateInfo/useUpdateDisplayInfo';
import { useUpdateWelcomeInfo } from './updateInfo/useUpdateWelcomeInfo';
import { useUpdateSoundInfo } from './updateInfo/useUpdateSoundInfo';
import { useUpdateMissionInfo } from './updateInfo/useUpdateMissionInfo';
import { useAutonomousKartInfo } from './updateInfo/useAutonomousKartInfo';

import { useIsDriver } from '../../hooks/useIsDriver';

export const useUpdateLiveData = () => {
	const socket = useSocket();

	// No need to subscribe to state in this hook - we'll use transient updates
	// This prevents re-renders of this component on every state change

	const isDriver = useIsDriver();
	const sendUserInput = useSendUserInput();

	// Import all update functions from separate modules
	const { checkVersion } = useCheckVersion();
	const { updateHudInfo } = useUpdateHudInfo();
	const { updateModeInfo } = useUpdateModeInfo();
	const { updateRemoteMode } = useUpdateRemoteMode();
	const { updateLocationInfo } = useUpdateLocationInfo();
	const { updateGoalInfo } = useUpdateGoalInfo();
	const { updateSystemInfo } = useUpdateSystemInfo();
	const { updateLoggerInfo } = useUpdateLoggerInfo();
	const { updateMapInfo } = useUpdateMapInfo();
	const { updateDisplayInfo } = useUpdateDisplayInfo();
	const { updateWelcomeInfo } = useUpdateWelcomeInfo();
	const { updateSoundInfo } = useUpdateSoundInfo();
	const { updateMissionInfo } = useUpdateMissionInfo();
	const { updateAutonomousKartInfo } = useAutonomousKartInfo();

	const sendLifeSignal = useCallback(() => {
		sendUserInput({ type: 'user_input', text: 'life_check', value: true });
	}, [sendUserInput]);

	// Direct update - runs at 20Hz without triggering React re-renders
	const onUpdate = useCallback(() => {
		// const startTime = performance.now();
		const frame = socket.getCurrentFrame();

		if (frame) {
			// Update frame store with streams data
			frameStore.getState().update(frame.streams);

			// Get current data directly from stores
			const prevData = {
				hud: hudStore.getState().data,
				mode: modeStore.getState().data,
				map: mapStore.getState().data,
				location: locationStore.getState().data,
				display: displayStore.getState().data,
				goal: goalStore.getState().data,
				system: systemStore.getState().data,
				sound: soundStore.getState().data,
				logger: loggerStore.getState().data,
				welcome: welcomeStore.getState().data,
				remote: remoteStore.getState().data,
				version: versionStore.getState().data,
				mission: missionStore.getState().data,
				autonomousKart: autonomousKartStore.getState().data,
			};

			if (prevData.version.checked) {
				// Create a mutable data object for update functions
				// Note: data is needed because some update functions reference other updated values
				// e.g., updateDisplayInfo references data.hud.mode, data.mode.run
				let data = { ...prevData };

				// Update mode
				data.mode = updateModeInfo(data, frame.streams);
				modeStore.getState().setState(data.mode);

				// Update goal (only for drivers)
				if (isDriver) {
					data.goal = updateGoalInfo(data, frame.streams);
					goalStore.getState().setState(data.goal);
				}

				// Update hud (high-frequency)
				data.hud = updateHudInfo(data, frame.streams);
				hudStore.getState().setState(data.hud);

				// Update location (high-frequency)
				data.location = updateLocationInfo(data, frame);
				locationStore.getState().setState(data.location);

				// Update map
				data.map = updateMapInfo(data, frame.streams);
				mapStore.getState().setState(data.map);

				// Update display (depends on hud.mode, mode.run)
				data.display = updateDisplayInfo(data, prevData);
				displayStore.getState().setState(data.display);

				// Update system and logger (only for drivers)
				if (isDriver) {
					data.system = updateSystemInfo(data, frame.streams);
					systemStore.getState().setState(data.system);

					data.logger = updateLoggerInfo(data, frame.streams);
					loggerStore.getState().setState(data.logger);
				}

				// Update welcome
				data.welcome = updateWelcomeInfo(data, frame.streams);
				welcomeStore.getState().setState(data.welcome);

				// Update sound and welcome (only for drivers)
				if (isDriver) {
					const { newSound, updatedWelcome } = updateSoundInfo(data, prevData);
					soundStore.getState().setState(newSound);
					welcomeStore.getState().setState(updatedWelcome);
					data.sound = newSound;
					data.welcome = updatedWelcome;

					// Update remote
					data.remote = updateRemoteMode(data, frame.streams);
					remoteStore.getState().setState(data.remote);
				}

				data.mission = updateMissionInfo(data, frame.streams);
				missionStore.getState().setState(data.mission);

				data.autonomousKart = updateAutonomousKartInfo(data, frame.streams);
				autonomousKartStore.getState().setState(data.autonomousKart);
			} else {
				// Check version
				const newVersion = checkVersion(frame.streams, prevData.version);
				versionStore.getState().setState(newVersion);
			}
		}
		// const endTime = performance.now();
		// console.log(`onUpdate took ${endTime - startTime} milliseconds`);
	}, [
		socket,
		isDriver,
		updateModeInfo,
		updateGoalInfo,
		updateHudInfo,
		updateLocationInfo,
		updateMapInfo,
		updateDisplayInfo,
		updateSystemInfo,
		updateLoggerInfo,
		updateWelcomeInfo,
		updateSoundInfo,
		updateRemoteMode,
		checkVersion,
	]);

	useEffect(() => {
		socket.on('update', onUpdate);
		return () => {
			socket.off('update', onUpdate);
		};
	}, [socket, onUpdate]);

	useEffect(() => {
		const interval = setInterval(() => {
			sendLifeSignal();
		}, 1000);
		return () => clearInterval(interval);
	}, [sendLifeSignal]);

	useEffect(() => {
		sendUserInput({ type: 'user_input', text: 'request_config', value: 1 });
	}, [sendUserInput]);
};
